import axios from "axios";
import { toast } from "react-toastify";
import { persistAuth, clearPersistedAuth, setPersistEnabled } from "../utils/authPersist";

const TOKEN_KEY = "token";
const REFRESH_KEY = "refreshToken";
const ROLE_KEY = "role";

// Read session first, fall back to the persisted localStorage copy. On a mobile
// PWA relaunch sessionStorage starts empty; the fallback means auth survives even
// if restoreAuth() hasn't repopulated sessionStorage yet.
export function getToken() {
  return sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY);
}
export function getRefreshToken() {
  return sessionStorage.getItem(REFRESH_KEY) || localStorage.getItem(REFRESH_KEY);
}
export function setTokens({ token, refreshToken }, { remember = true } = {}) {
  setPersistEnabled(remember);
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  if (refreshToken) sessionStorage.setItem(REFRESH_KEY, refreshToken);
  if (remember) persistAuth();
  else clearPersistedAuth();
}
export function setRole(role) {
  if (role) sessionStorage.setItem(ROLE_KEY, role);
  persistAuth();
}
export function getRole() {
  return sessionStorage.getItem(ROLE_KEY) || localStorage.getItem(ROLE_KEY);
}
export function clearAuth() {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(REFRESH_KEY);
  sessionStorage.removeItem(ROLE_KEY);
  sessionStorage.removeItem("employeeid");
  sessionStorage.removeItem("name");
  clearPersistedAuth(); // also wipe the localStorage copy so logout truly logs out
}

const api = axios.create({
  baseURL: "",
  timeout: 30000,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Let the browser set multipart boundary — default application/json breaks file uploads.
  if (config.data instanceof FormData) {
    delete config.headers["Content-Type"];
  }
  return config;
});

let refreshPromise = null;

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getRefreshToken();
  if (!refreshToken) throw new Error("No refresh token");
  refreshPromise = axios
    .post("/api/auth/refresh", { refreshToken })
    .then((res) => {
      const newToken = res.data.token;
      if (newToken) { sessionStorage.setItem(TOKEN_KEY, newToken); persistAuth(); }
      return newToken;
    })
    .finally(() => {
      refreshPromise = null;
    });
  return refreshPromise;
}

// Exposed so the app can proactively refresh on cold start (an expired 8h access
// token with a still-valid 7-day refresh token should self-heal, not log out).
export { refreshAccessToken };

function forceLogout(error) {
  clearAuth();
  const path = window.location.pathname;
  if (path !== "/login" && path !== "/") {
    toast.error("Session expired. Please log in again.");
    window.location.href = `/login?redirect=${encodeURIComponent(path)}`;
  }
  return Promise.reject(error);
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const code = error.response?.data?.code;

    // Expired access token → try the refresh token. Only a GENUINE refresh
    // failure logs out. When ~10 dashboard requests 401 at once, they share the
    // single-flight refresh (getRefreshToken/refreshAccessToken) and each
    // retries with the new token — a sibling's stale 401 must NOT trigger
    // logout after the refresh already succeeded, which is why the old blanket
    // `if (status === 401) clearAuth()` fall-through was the bug.
    if (status === 401 && code === "TOKEN_EXPIRED" && !original._retry && getRefreshToken()) {
      original._retry = true;
      let newToken;
      try {
        newToken = await refreshAccessToken();
      } catch (_) {
        return forceLogout(error); // refresh token itself invalid/expired → real logout
      }
      if (newToken) {
        original.headers = original.headers || {};
        original.headers.Authorization = `Bearer ${newToken}`;
        return api(original);
      }
      return forceLogout(error);
    }

    // Any other 401 (invalid/revoked token, missing auth, or a retry that still
    // 401'd) is unrecoverable → log out.
    if (status === 401) {
      return forceLogout(error);
    }

    if (status === 403) {
      // Module-toggle 403s are expected background noise (e.g. the dashboard
      // probing a disabled module's endpoint) — pages handle them themselves.
      if (error.response?.data?.code !== "MODULE_DISABLED") {
        toast.error(error.response?.data?.message || "You don't have permission to do that.");
      }
    } else if (status >= 500) {
      toast.error("Server error. Please try again later.");
    } else if (status === 429) {
      toast.warn(error.response?.data?.message || "Too many requests. Slow down.");
    } else if (error.response?.data?.message && status >= 400) {
      // Let caller decide — don't auto-toast 4xx validation
    } else if (!error.response) {
      toast.error("Network error. Check your connection.");
    }

    return Promise.reject(error);
  }
);

export const apiGet = (url, config) => api.get(url, config).then((r) => r.data);
export const apiPost = (url, data, config) => api.post(url, data, config).then((r) => r.data);
export const apiPut = (url, data, config) => api.put(url, data, config).then((r) => r.data);
export const apiPatch = (url, data, config) => api.patch(url, data, config).then((r) => r.data);
export const apiDelete = (url, config) => api.delete(url, config).then((r) => r.data);

export default api;
